import {useMemo} from 'react';
import PropTypes from 'prop-types';

import {Text, Box, Image as BaseImage, useMultiStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui';

import {useResponsiveValue} from '@amblique/base/app/hooks/use-responsive-value';
import {inflector} from '@amblique/base/shared/helpers/inflector';
import Link from '@amblique/king-living/app/components/link';

/**
 * Image component
 *
 * @param {Object} props - Component props
 * @param {Contentful.Image} props.component - Image component data
 * @param {boolean} [props.isComplete] - Whether component has all fields
 * @param {boolean} [props.isFetching] - Whether additional data is being fetched
 * @returns {React.ReactElement}
 */
export const Image = ({component, isComplete = true, isFetching = false, ...props}) => {
  if (!isComplete && isFetching) {
    return null;
  }

  const {
    mobileAsset,
    tabletAsset,
    desktopAsset,
    altText,
    url,
    openInNewTab,
    fit,
    focus,
    format,
    description,
  } = component;

  const styles = useMultiStyleConfig('contentful/image', {component, isComplete, isFetching});
  const assetUrl = useMemo(() => {
    const urls = [];

    if (mobileAsset?.url) {
      urls.push(`mobile: ${mobileAsset.url}`);
    } else if (tabletAsset?.url) {
      urls.push(`mobile: ${tabletAsset.url}`);
    } else if (desktopAsset?.url) {
      urls.push(`mobile: ${desktopAsset.url}`);
    }

    if (tabletAsset?.url && mobileAsset?.url) {
      urls.push(`tablet: ${tabletAsset.url}`);
    }

    if (desktopAsset?.url && (tabletAsset?.url || mobileAsset?.url)) {
      urls.push(`desktop: ${desktopAsset.url}`);
    }

    if (urls.length === 0) {
      return '';
    }

    // Return responsive string format
    return urls.join(', ');
  }, [mobileAsset, tabletAsset, desktopAsset]);

  // Parse responsive values to get current matched values
  const width = useResponsiveValue(component.width, 'value');
  const height = useResponsiveValue(component.height, 'value');
  const quality = useResponsiveValue(component.quality, 'value');
  const imageUrl = useResponsiveValue(assetUrl, 'value', 'string');

  // Build current src with current params
  const src = useMemo(() => {
    if (!imageUrl) return '';

    const params = {};

    if (fit) {
      params.fit = fit.toLowerCase();

      if (focus && (fit === 'fill' || fit === 'crop' || fit === 'thumb')) {
        params.f = inflector.tableize(focus);
      }
    }

    if (format) {
      params.fm = format.toLowerCase();
    }

    if (width) {
      params.w = width;
    }

    if (height) {
      params.h = height;
    }

    if (quality) {
      params.q = quality;
    }

    const url = new URL(imageUrl);

    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        url.searchParams.set(key, String(value));
      }
    });

    return url.toString();
  }, [imageUrl, fit, format, width, height, quality]);

  return (
    <Box {...styles.root} {...props}>
      {url ? (
        <Link target={openInNewTab ? '_blank' : '_self'} to={url}>
          <BaseImage alt={altText} src={src} {...styles.image} />
          {description && (<Text {...styles.description}>{description}</Text>)}
        </Link>
      ) : (
        <>
          <BaseImage alt={altText} src={src} {...styles.image} />
          {description && (<Text {...styles.description}>{description}</Text>)}
        </>
      )}
    </Box>
  );
};

Image.displayName = 'Image';
Image.propTypes = {
  component: PropTypes.object.isRequired,
  isComplete: PropTypes.bool,
  isFetching: PropTypes.bool,
};
